/**
 * Stripe Collection Credentials — per-workspace editor.
 *
 * Shown directly below the existing "Stripe (card / international)" toggle
 * in `PaymentsTab.jsx` whenever `stripe_enabled === true`. Gated to
 * owner + admin by the backend (`/api/settings/stripe-credentials`).
 *
 * Design rules from the iter-61 spec:
 *   • Test / Live are stored side-by-side — switching the mode toggle
 *     NEVER wipes the other mode's saved credentials.
 *   • Secret + webhook fields are write-only on the wire. GET returns
 *     only `configured` + last-4 hint + last-updated audit.
 *   • Live cannot be activated until (a) all three Live fields are saved
 *     AND (b) a successful Verify Connection is recorded for Live.
 *   • Every save invalidates the row's Verify status — user must re-verify.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard,
  CheckCircle,
  Warning,
  XCircle,
  PencilSimple,
  FloppyDisk,
  Lightning,
} from "@phosphor-icons/react";

const MODES = [
  { key: "test", label: "Test Mode" },
  { key: "live", label: "Live Mode" },
];

const STATUS_STYLE = {
  disabled:                { icon: XCircle,      cls: "text-muted-foreground bg-muted/50 border-muted" },
  credentials_incomplete:  { icon: Warning,      cls: "text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200" },
  verification_required:   { icon: Warning,      cls: "text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200" },
  connected_test:          { icon: CheckCircle,  cls: "text-blue-700 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200" },
  connected_live:          { icon: CheckCircle,  cls: "text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200" },
  connection_error:        { icon: XCircle,      cls: "text-red-700 bg-red-50 border-red-200 dark:bg-red-950/30 dark:text-red-200" },
};

export default function StripeCredentialsSection({ stripeEnabled }) {
  const [view, setView] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState("test");        // "test" | "live"  (the tab the operator is EDITING, not necessarily active_mode)
  const [editing, setEditing] = useState({ publishable_key: false, secret_key: false, webhook_secret: false });
  const [draft, setDraft] = useState({ publishable_key: "", secret_key: "", webhook_secret: "" });
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [switching, setSwitching] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payments/settings/stripe-credentials");
      setView(data);
      // Follow the workspace's real active mode when the user first opens
      // the panel, but let them freely tab between modes afterwards.
      if (!loaded) setMode(data.active_mode);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoaded(true); }
  };
  useEffect(() => { if (stripeEnabled) load(); /* eslint-disable-next-line */ }, [stripeEnabled]);

  if (!stripeEnabled) return null;
  if (!loaded || !view) {
    return (
      <div className="border border-dashed rounded-md p-4 text-sm text-muted-foreground" data-testid="stripe-creds-loading">
        Loading Stripe credentials…
      </div>
    );
  }

  const activeMode = view.active_mode;
  const editingRow = view[mode] || {};
  const status = view.status;
  const statusStyle = STATUS_STYLE[status?.code] || STATUS_STYLE.disabled;
  const StatusIcon = statusStyle.icon;

  const toggleEdit = (field) => setEditing(e => ({ ...e, [field]: !e[field] }));

  const onChange = (field, value) => setDraft(d => ({ ...d, [field]: value }));

  const anyDraft = draft.publishable_key || draft.secret_key || draft.webhook_secret;
  const hasChanges = Object.values(editing).some(Boolean) && anyDraft;

  const save = async () => {
    if (!hasChanges) { toast.error("Nothing to save"); return; }
    setSaving(true);
    try {
      const payload = { mode };
      // Only send fields the operator explicitly re-typed. Empty means "keep existing" on the backend.
      if (editing.publishable_key) payload.publishable_key = draft.publishable_key || "";
      if (editing.secret_key)      payload.secret_key      = draft.secret_key      || "";
      if (editing.webhook_secret)  payload.webhook_secret  = draft.webhook_secret  || "";
      const { data } = await api.put("/payments/settings/stripe-credentials", payload);
      setView(data);
      toast.success(`Saved ${mode === "test" ? "Test" : "Live"} Mode credentials — re-verify to reactivate.`);
      setDraft({ publishable_key: "", secret_key: "", webhook_secret: "" });
      setEditing({ publishable_key: false, secret_key: false, webhook_secret: false });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/payments/settings/stripe-credentials/verify", { mode });
      setView(data.view);
      if (data.status === "connected") toast.success(data.message || "Verified");
      else toast.error(data.message || "Verification failed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setVerifying(false); }
  };

  const activate = async (newMode) => {
    if (newMode === activeMode) return;
    setSwitching(true);
    try {
      const { data } = await api.put("/payments/settings/stripe-credentials/mode", { mode: newMode });
      setView(data);
      toast.success(data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSwitching(false); }
  };

  return (
    <Card className="border-primary/20 bg-primary/[0.02]" data-testid="stripe-creds-section">
      <CardContent className="p-4 space-y-4">
        {/* Header — connection status pill + heading */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <CreditCard size={16} weight="duotone" /> Stripe Collection Credentials
            </div>
            <p className="text-[11px] text-muted-foreground mt-1 max-w-xl">
              These credentials are used ONLY to collect payments from your workspace&apos;s parents / clients into your own Stripe account. They are never used for TherapyStack Pro subscription billing.
            </p>
          </div>
          <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${statusStyle.cls}`} data-testid="stripe-creds-status">
            <StatusIcon size={12} weight="fill" />
            {status?.label || "—"}
          </div>
        </div>
        {status?.message && (
          <div className="text-[11px] text-muted-foreground -mt-1">{status.message}</div>
        )}

        {/* "Currently Using" banner — always visible regardless of which mode is being edited. */}
        <div
          className={`rounded-md border p-2.5 text-xs font-medium flex items-center gap-2 ${
            activeMode === "live"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200"
              : "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950/30 dark:text-blue-200"
          }`}
          data-testid="stripe-creds-active-banner"
        >
          <Lightning size={13} weight="fill" />
          Currently Using Stripe {activeMode === "live" ? "Live Mode" : "Test Mode"}
        </div>

        {/* Mode tabs */}
        <div className="flex items-center gap-1 rounded-md border bg-card p-1 text-xs w-fit" data-testid="stripe-creds-mode-tabs">
          {MODES.map(m => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              className={`px-3 py-1.5 rounded ${mode === m.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`stripe-creds-tab-${m.key}`}
            >
              {m.label}
              {view[`${m.key}_complete`] && <CheckCircle size={11} weight="fill" className="inline ml-1.5 opacity-75" />}
            </button>
          ))}
          <div className="ml-2 pl-2 border-l text-[11px] text-muted-foreground">
            Editing:&nbsp;
            <span className="font-medium text-foreground">{mode === "live" ? "Live" : "Test"}</span>
          </div>
        </div>

        {/* Field editor */}
        <div className="grid grid-cols-1 gap-3">
          <StripeCredField
            label={mode === "live" ? "Live Publishable Key" : "Test Publishable Key"}
            hint={`Public key — starts with '${mode === "live" ? "pk_live_" : "pk_test_"}'`}
            stored={editingRow.publishable_key}
            storedLabel={editingRow.publishable_key || "—"}
            secret={false}
            editing={editing.publishable_key}
            draft={draft.publishable_key}
            onEditToggle={() => toggleEdit("publishable_key")}
            onChange={(v) => onChange("publishable_key", v)}
            testId={`stripe-creds-pk-${mode}`}
          />
          <StripeCredField
            label={mode === "live" ? "Live Secret Key" : "Test Secret Key"}
            hint={`Server-side secret — never shown again after save. Starts with '${mode === "live" ? "sk_live_" : "sk_test_"}'`}
            stored={editingRow.secret_key?.set}
            storedLabel={editingRow.secret_key?.set ? `Configured · ••••${editingRow.secret_key.hint}` : "Not configured"}
            secret
            editing={editing.secret_key}
            draft={draft.secret_key}
            onEditToggle={() => toggleEdit("secret_key")}
            onChange={(v) => onChange("secret_key", v)}
            testId={`stripe-creds-sk-${mode}`}
          />
          <StripeCredField
            label="Webhook Signing Secret"
            hint="From Stripe → Developers → Webhooks. Starts with 'whsec_'."
            stored={editingRow.webhook_secret?.set}
            storedLabel={editingRow.webhook_secret?.set ? `Configured · ••••${editingRow.webhook_secret.hint}` : "Not configured"}
            secret
            editing={editing.webhook_secret}
            draft={draft.webhook_secret}
            onEditToggle={() => toggleEdit("webhook_secret")}
            onChange={(v) => onChange("webhook_secret", v)}
            testId={`stripe-creds-whsec-${mode}`}
          />
        </div>

        {/* Audit trail for the row */}
        {editingRow.last_updated_at && (
          <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3" data-testid="stripe-creds-audit">
            <span>Last updated {new Date(editingRow.last_updated_at).toLocaleString()}</span>
            {editingRow.last_updated_by_email && <span>by <span className="font-medium">{editingRow.last_updated_by_email}</span></span>}
            {editingRow.verified_status && (
              <span className={editingRow.verified_status === "connected" ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-300"}>
                · {editingRow.verified_status === "connected" ? "verified" : "verify failed"}
                {editingRow.verified_at && <> at {new Date(editingRow.verified_at).toLocaleString()}</>}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-border">
          <div className="flex items-center gap-2">
            <Button
              size="sm" onClick={save}
              disabled={!hasChanges || saving}
              data-testid={`stripe-creds-save-${mode}`}
            >
              <FloppyDisk size={13} className="mr-1.5" /> {saving ? "Saving…" : `Save ${mode === "live" ? "Live" : "Test"}`}
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={verify}
              disabled={verifying || !view[`${mode}_complete`]}
              title={!view[`${mode}_complete`] ? `Save all three ${mode === "live" ? "Live" : "Test"} fields first` : ""}
              data-testid={`stripe-creds-verify-${mode}`}
            >
              <Lightning size={13} className="mr-1.5" /> {verifying ? "Verifying…" : "Verify Connection"}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">
              Active: {activeMode === "live" ? "Live Mode" : "Test Mode"}
            </Badge>
            {activeMode === "test" ? (
              <Button
                variant="default" size="sm"
                onClick={() => activate("live")}
                disabled={switching || !view.live_complete || view.live?.verified_status !== "connected"}
                title={
                  !view.live_complete ? "Save all three Live Mode fields first"
                  : view.live?.verified_status !== "connected" ? "Verify Live Mode connection first"
                  : "Switch this workspace's Stripe collection to Live Mode"
                }
                data-testid="stripe-creds-activate-live"
              >
                Activate Live Mode
              </Button>
            ) : (
              <Button
                variant="outline" size="sm"
                onClick={() => activate("test")}
                disabled={switching}
                data-testid="stripe-creds-activate-test"
              >
                Switch back to Test Mode
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function StripeCredField({
  label, hint, stored, storedLabel, secret,
  editing, draft, onEditToggle, onChange, testId,
}) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        <Button
          variant="ghost" size="sm" onClick={onEditToggle}
          className="h-6 px-2 text-[11px]"
          data-testid={`${testId}-toggle`}
        >
          <PencilSimple size={11} className="mr-1" /> {editing ? "Cancel" : (stored ? "Update" : "Enter")}
        </Button>
      </div>
      {editing ? (
        <Input
          type={secret ? "password" : "text"}
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          placeholder={hint}
          autoComplete="off"
          data-testid={`${testId}-input`}
        />
      ) : (
        <div className="text-xs px-2.5 py-1.5 rounded border bg-muted/40 font-mono truncate">
          {storedLabel}
        </div>
      )}
      <div className="text-[10px] text-muted-foreground">{hint}</div>
    </div>
  );
}
